/**
 * "Raspored slika" — the collage board's rules (collage/board.ts).
 *
 * A self-contained block on `.pz-board*` classes, appended to the widget's
 * sheet by styles.ts. Full screen on a phone (the sheet is the VISIBLE
 * viewport, so the action bar never slides under the URL bar or the home
 * indicator), a large dialog on a desktop, the action bar in a right-hand
 * column on a phone turned on its side. Touch targets are at least 44 px.
 *
 * Inside the stage the sheet is scaled with a CSS transform (the "world"), so
 * anything that must stay the same size on screen — badges, rings, dashed
 * outlines — is multiplied by `--pzb-inv` (1 / zoom), which board.ts sets.
 */
export const BOARD_STYLES = `
/* ── "Raspored slika": the collage board (collage/board.ts) ───────────────── */
/* Two classes: dialog.pz-layer resets padding, background and size for every layer. */
.pz-board.pz-layer { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483300;
  display: grid; place-items: stretch; padding: 0; }
.pz-board[hidden] { display: none; }
.pz-board.pz-layer--fixed { background: rgba(12,12,20,.72); }
.pz-board-card { position: relative; display: flex; flex-direction: column; min-width: 0; min-height: 0;
  width: 100%; height: 100vh; height: 100dvh; height: var(--pz-vvh, 100dvh); max-height: 100%;
  overflow: hidden; background: var(--pz-surface, #fff); color: var(--pz-ink, #16161d);
  font: 14px/1.4 var(--pz-font); }
.pz-board-head { flex: none; display: flex; align-items: center; gap: 10px; min-height: 52px;
  padding: 6px 10px 6px 14px; padding-top: max(6px, env(safe-area-inset-top));
  padding-left: max(14px, env(safe-area-inset-left)); padding-right: max(10px, env(safe-area-inset-right));
  border-bottom: 1px solid var(--pz-line); background: var(--pz-surface, #fff); }
.pz-board-title { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.pz-board-title strong { font-size: 16px; font-weight: 700; line-height: 1.25;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-board-title span { font-size: 12px; color: var(--pz-muted); line-height: 1.3;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-board-count { flex: none; padding: 4px 9px; border-radius: 999px; background: var(--pz-surface-2, #f6f6f8);
  font-size: 13px; font-weight: 650; font-variant-numeric: tabular-nums; color: var(--pz-ink-2); }
.pz-board-count.is-complete { color: var(--pz-success, #1b7a4a); }
.pz-btn.pz-board-done { flex: none; min-height: 44px; padding: 8px 18px; font-size: 15px; }

/* A collage on several print sheets: the switcher between the header and the stage. */
.pz-board-sheets { flex: none; display: flex; gap: 4px; margin: 0; padding: 6px 10px;
  padding-left: max(10px, env(safe-area-inset-left)); padding-right: max(10px, env(safe-area-inset-right));
  border-bottom: 1px solid var(--pz-line); background: var(--pz-surface, #fff); overflow-x: auto; scrollbar-width: none; }
.pz-board-sheets::-webkit-scrollbar { display: none; }
.pz-board-sheettab { position: relative; flex: 1 1 0; min-width: max-content; min-height: 40px; margin: 0; padding: 6px 12px;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: 1.5px solid var(--pz-line-2); border-radius: 10px; background: var(--pz-surface, #fff); color: var(--pz-ink-2);
  font: 600 13.5px/1.2 var(--pz-font); white-space: nowrap; cursor: pointer; -webkit-tap-highlight-color: transparent; }
.pz-board-sheettab-count { font-weight: 500; color: var(--pz-muted); }
.pz-board-sheettab-count::before { content: "· "; }
.pz-board-sheettab.is-active { border-color: var(--pz-accent); background: var(--pz-accent-veil, rgba(81,69,205,.1));
  color: var(--pz-ink); box-shadow: 0 0 0 2px var(--pz-accent-ring, rgba(81,69,205,.18)); }
.pz-board-sheettab.has-empty::after { content: ""; position: absolute; top: 5px; right: 5px; width: 7px; height: 7px;
  border-radius: 50%; background: var(--pz-warn, #9a5b05); }
.pz-board-sheettab:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-board-card.is-dragging .pz-board-sheettab:not(.is-active) { border-style: dashed; border-color: var(--pz-accent); }

.pz-board-main { position: relative; flex: 1 1 auto; min-height: 0; display: grid;
  grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) auto; }
.pz-board-view { position: relative; min-width: 0; min-height: 0; overflow: hidden; background: #e8e9ee; }
.pz-board-stage { position: absolute; top: 0; right: 0; bottom: 0; left: 0; overflow: hidden;
  touch-action: none; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
  cursor: grab; outline: 0; }
.pz-board-card.is-mouse .pz-board-stage { cursor: default; }
.pz-board-stage.is-panning { cursor: grabbing; }
.pz-board-world { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
.pz-board-world.is-animated { transition: transform .18s var(--pz-ease, ease-out); }
.pz-board-sheetworld { background: #fff; box-shadow: 0 1px 3px rgba(16,16,32,.14), 0 10px 30px -10px rgba(16,16,32,.3); }
.pz-board-sheet { position: absolute; top: 0; left: 0; display: block; width: 100%; height: 100%;
  max-width: none; max-height: none; margin: 0; border: 0; background: transparent; }
.pz-board-card.is-standin .pz-board-sheet, .pz-board-card.is-loading .pz-board-sheet { visibility: hidden; }
.pz-board-standins { position: absolute; top: 0; right: 0; bottom: 0; left: 0; pointer-events: none; }
.pz-board-standin { position: absolute; overflow: hidden; opacity: 0; background: #eef0f4;
  transition: opacity .3s ease-out; -webkit-mask-size: 100% 100%; mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; }
.pz-board-standin img { position: absolute; display: block; max-width: none; max-height: none; margin: 0;
  user-select: none; pointer-events: none; }
.pz-board-standin.is-cover img { top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }
.pz-board-standin.is-empty img { display: none; }
.pz-board-flip { position: absolute; top: 0; right: 0; bottom: 0; left: 0; }
.pz-board-standin.is-pending,
.pz-board-card.is-standin .pz-board-standin,
.pz-board-card.is-loading .pz-board-standin { opacity: 1; transition: none; }
.pz-board-card.is-standin .pz-board-standin { box-shadow: inset 0 0 0 calc(1px * var(--pzb-inv, 1)) #c3c6d1; }
.pz-board-standin.is-flip { transition: transform .18s var(--pz-ease, ease-out); }
.pz-board-detail { position: absolute; left: 0; top: 0; display: block; pointer-events: none;
  max-width: none; max-height: none; margin: 0; border: 0; background: transparent; }
.pz-board-hits { pointer-events: none; }

.pz-board-slot { position: absolute; display: block; margin: 0; padding: 0; border: 0; border-radius: 0;
  background: transparent; color: inherit; font: inherit; pointer-events: auto; cursor: pointer;
  -webkit-tap-highlight-color: transparent; outline: 0; }
.pz-board-slot.is-round { border-radius: 50%; }
.pz-board-slot.is-empty { background: rgba(236,237,242,.88);
  outline: calc(2px * var(--pzb-inv, 1)) dashed #9a9eb0; outline-offset: calc(-2px * var(--pzb-inv, 1)); }
.pz-board-slot[aria-pressed="true"] { z-index: 2;
  box-shadow: 0 0 0 calc(3px * var(--pzb-inv, 1)) var(--pz-accent), 0 0 0 calc(5px * var(--pzb-inv, 1)) rgba(255,255,255,.92); }
.pz-board-slot:focus-visible { outline: calc(2px * var(--pzb-inv, 1)) solid var(--pz-accent);
  outline-offset: calc(2px * var(--pzb-inv, 1)); }
.pz-board-card.is-mouse .pz-board-slot:not(.is-empty):hover { box-shadow: inset 0 0 0 calc(2px * var(--pzb-inv, 1)) rgba(255,255,255,.85); }
.pz-board-card.is-mouse .pz-board-slot[aria-pressed="true"]:hover {
  box-shadow: 0 0 0 calc(3px * var(--pzb-inv, 1)) var(--pz-accent), 0 0 0 calc(5px * var(--pzb-inv, 1)) rgba(255,255,255,.92); }
.pz-board-plus { position: absolute; left: 50%; top: 50%; display: flex; flex-direction: column; align-items: center;
  transform: translate(-50%, -50%) scale(var(--pzb-inv, 1)); color: #585c6d; font-size: 12px; font-weight: 700;
  line-height: 1.1; pointer-events: none; }
.pz-board-plus b { font-size: 22px; font-weight: 600; line-height: 1; }
.pz-board-badges { position: absolute; top: calc(4px * var(--pzb-inv, 1)); right: calc(4px * var(--pzb-inv, 1));
  display: flex; gap: 3px; transform: scale(var(--pzb-inv, 1)); transform-origin: 100% 0; pointer-events: none; }
.pz-board-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px;
  padding: 0 5px; border-radius: 999px; background: rgba(22,22,29,.78); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1; box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.pz-board-badge svg { display: block; }
.pz-board-badge.is-face { padding: 0 3px; background: #fff; color: var(--pz-warn, #9a5b05); }
.pz-board-badge.is-check { padding: 0 4px; background: var(--pz-accent); color: #fff; }
.pz-board-card.is-swapping .pz-board-slot:not([aria-pressed="true"]) { outline: calc(2px * var(--pzb-inv, 1)) dashed var(--pz-accent);
  outline-offset: calc(-2px * var(--pzb-inv, 1)); }
.pz-board-hint.is-swap { color: var(--pz-ink, inherit); font-weight: 600; }
.pz-board-badge.is-soft { width: 16px; min-width: 16px; height: 16px; padding: 0; background: rgba(255,255,255,.94);
  color: #676774; font: italic 700 10px/1 Georgia, serif; }
.pz-board-card.is-dragging .pz-board-slot:not(.is-lifted) { outline: calc(2px * var(--pzb-inv, 1)) dashed var(--pz-accent);
  outline-offset: calc(-2px * var(--pzb-inv, 1)); }
.pz-board-slot.is-lifted { background: rgba(255,255,255,.55); }
.pz-board-slot.is-drop-hover { background: var(--pz-accent-veil, rgba(81,69,205,.28));
  box-shadow: 0 0 0 calc(4px * var(--pzb-inv, 1)) var(--pz-accent); }
.pz-board-slot.is-flash { animation: pz-board-flash .4s ease-out; }
@keyframes pz-board-flash { from { background: rgba(255,255,255,.7); } to { background: transparent; } }

.pz-board-ghost { position: absolute; left: 0; top: 0; z-index: 6; width: 72px; height: 72px; border-radius: 10px;
  background: #fff center / cover no-repeat; box-shadow: 0 10px 28px rgba(0,0,0,.38), 0 0 0 2px #fff;
  opacity: .94; pointer-events: none; }
.pz-board-zoom { position: absolute; right: 10px; bottom: 10px; z-index: 4; display: flex; gap: 6px; }
.pz-board-zbtn { display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-width: 44px; height: 44px; padding: 0 10px; margin: 0; border: 0; border-radius: 12px;
  background: rgba(255,255,255,.95); color: var(--pz-ink); box-shadow: var(--pz-shadow-md);
  font: 600 13px/1 var(--pz-font); cursor: pointer; -webkit-tap-highlight-color: transparent; }
.pz-board-zbtn:disabled { opacity: .5; cursor: default; }
.pz-board-zbtn:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-board-zbtn span { display: none; }
.pz-board-card.is-mouse .pz-board-zbtn span { display: inline; }
.pz-board-loading { position: absolute; left: 50%; top: 12px; z-index: 4; transform: translateX(-50%);
  padding: 6px 12px; border-radius: 999px; background: rgba(22,22,29,.82); color: #fff;
  font-size: 12.5px; white-space: nowrap; pointer-events: none; }

.pz-board-side { display: flex; flex-direction: column; min-width: 0; min-height: 0;
  border-top: 1px solid var(--pz-line); background: var(--pz-surface, #fff);
  padding-bottom: env(safe-area-inset-bottom); padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right); }
.pz-board-spares { flex: none; display: flex; align-items: center; gap: 8px; height: 56px; padding: 0 12px;
  border-bottom: 1px solid var(--pz-line); overflow-x: auto; overflow-y: hidden; touch-action: pan-x;
  scrollbar-width: none; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; }
.pz-board-spares::-webkit-scrollbar { display: none; }
.pz-board-spares-title { flex: none; font-size: 12px; font-weight: 700; color: var(--pz-muted); }
.pz-board-spare { flex: none; width: 44px; height: 44px; margin: 0; padding: 0; border: 2px solid #fff; border-radius: 8px;
  background: var(--pz-surface-3, #ececf1) center / cover no-repeat; box-shadow: 0 0 0 1px var(--pz-line-2);
  cursor: pointer; -webkit-tap-highlight-color: transparent; }
.pz-board-spare[aria-pressed="true"] { box-shadow: 0 0 0 3px var(--pz-accent); }
.pz-board-spare.is-drop-hover { box-shadow: 0 0 0 3px var(--pz-accent), 0 0 0 6px var(--pz-accent-ring); }
.pz-board-spare:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-board-bar { display: grid; gap: 6px; padding: 8px 12px 10px; }
.pz-board-notice { margin: 8px 12px 0; padding: 7px 10px; border-radius: 10px; background: var(--pz-surface-3, #ececf1);
  color: var(--pz-ink-2); font-size: 12.5px; font-weight: 600; line-height: 1.45; overflow-wrap: anywhere; }
.pz-board-notice[hidden] { display: none; }
.pz-board-info { display: flex; align-items: center; gap: 6px; min-height: 40px; font-size: 14.5px; font-weight: 650; }
.pz-board-info > span { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.pz-board-nav { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px;
  margin: 0; padding: 0; border: 1.5px solid var(--pz-line-2); border-radius: 12px; background: var(--pz-surface, #fff);
  color: var(--pz-ink-2); cursor: pointer; -webkit-tap-highlight-color: transparent; }
.pz-board-nav:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-board-hint, .pz-board-advice { margin: 0; font-size: 12.5px; line-height: 1.45; overflow-wrap: anywhere; }
.pz-board-hint { color: var(--pz-muted); }
.pz-board-advice { color: var(--pz-ink-2); }
.pz-board-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.pz-board-actions .pz-btn-ghost { flex: 1 1 0; min-width: max-content; min-height: 44px; padding: 8px 10px; font-size: 13.5px; gap: 6px; }
.pz-board-actions .pz-btn-ghost svg { flex: none; }
.pz-board-live { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0; }

/* A phone held upright: the actions sit in ONE row, icon over word, so a
   chosen slot's four ("Kadriraj", "Zameni mesto", "Nova slika", "Ukloni") do
   not take a second row from the sheet. Words break between words only. */
@media (max-width: 480px) and (orientation: portrait) {
  .pz-board-sheets { padding-top: 4px; padding-bottom: 4px; }
  .pz-board-sheettab { min-width: 0; min-height: 36px; padding: 4px 8px; font-size: 12.5px; gap: 4px; }
  .pz-board-actions { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: 6px; }
  .pz-board-actions .pz-btn-ghost { flex-direction: column; min-width: 0; min-height: 54px; padding: 6px 4px; gap: 3px;
    font-size: 12.5px; line-height: 1.15; text-align: center; overflow-wrap: normal; }
  .pz-board-bar { gap: 4px; padding-top: 6px; }
  .pz-board-info { min-height: 44px; }
}
/* The narrowest phones: a fifth button ("Automatski", for a hand framing)
   would not fit its word; the framer has the same reset. */
@media (max-width: 374px) and (orientation: portrait) {
  .pz-board-actions .pz-btn-ghost[data-action="auto"] { display: none; }
}
/* A phone on its side: the bar becomes a 200 px column on the right. */
@media (orientation: landscape) and (max-height: 499px) {
  .pz-board-head { min-height: 44px; padding-top: max(4px, env(safe-area-inset-top)); padding-bottom: 4px; }
  .pz-board-title span { display: none; }
  .pz-board-sheets { padding-top: 3px; padding-bottom: 3px; }
  .pz-board-sheettab { min-height: 32px; padding: 3px 10px; }
  .pz-btn.pz-board-done { min-height: 40px; }
  .pz-board-main { grid-template-rows: minmax(0, 1fr); grid-template-columns: minmax(0, 1fr) 200px; }
  .pz-board-side { border-top: 0; border-left: 1px solid var(--pz-line); overflow-y: auto; }
  .pz-board-actions .pz-btn-ghost { flex-basis: 100%; }
}
/* A desktop: a large dialog with the bar beside the sheet. */
@media (min-width: 900px) and (min-height: 560px) {
  .pz-board.pz-layer { place-items: center; }
  .pz-board-card { width: min(1100px, 96vw); height: 92vh; max-height: 92vh; border-radius: 16px;
    box-shadow: var(--pz-shadow-lg); animation: pz-pop-in .2s var(--pz-ease, ease-out); }
  .pz-board-head { padding-top: 6px; }
  .pz-board-main { grid-template-rows: minmax(0, 1fr); grid-template-columns: minmax(0, 1fr) 300px; }
  .pz-board-side { border-top: 0; border-left: 1px solid var(--pz-line); overflow-y: auto; padding-bottom: 0; }
  .pz-board-spares { height: auto; min-height: 56px; flex-wrap: wrap; padding: 8px 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .pz-board-world.is-animated, .pz-board-standin.is-flip, .pz-board-standin { transition: none; }
}
`;
